import React, { useState, useEffect } from 'react';
import {
  Search, Filter, Plus, Eye, Edit, Trash2, MapPin, Clock, Star,
  Users, ChevronLeft, ChevronRight, Loader, AlertCircle, Briefcase,
  TrendingUp, X, FileText, Building, Archive, RotateCcw
} from 'lucide-react';
import { useSelector } from 'react-redux';
import api from '../../../utils/Api';
import { useNavigate } from 'react-router-dom';

const CompanyJobOffers = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [jobOffers, setJobOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    contractType: '',
    experienceLevel: '',
    isFeatured: ''
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  const [stats, setStats] = useState({
    total: 0,
    featured: 0,
    totalViews: 0
  });

  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'archived'

  const [limits, setLimits] = useState({
    canCreate: true,
    message: '',
    isLoading: true
  });

  const companyId = user._id;

  useEffect(() => {
    fetchJobOffers();
    checkLimits();
  }, [activeTab]);

  const checkLimits = async () => {
    try {
      const response = await api.get('/jobOffer/limits');
      if (response.data.success) {
        setLimits({
          canCreate: response.data.canCreate,
          message: response.data.message,
          isLoading: false
        });
      }
    } catch (error) {
      console.error("Error checking limits", error);
      setLimits({ canCreate: true, message: '', isLoading: false }); // Fallback to allow? Or block? Safe to allow and let backend block.
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '' || filters.contractType || filters.experienceLevel || filters.isFeatured) {
        fetchJobOffers(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, filters]);

  const fetchJobOffers = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        status: activeTab,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.contractType && { contractType: filters.contractType }),
        ...(filters.experienceLevel && { experienceLevel: filters.experienceLevel }),
        ...(filters.isFeatured && { isFeatured: filters.isFeatured })
      });

      const response = await api.get(`/jobOffer/company/${companyId}?${params}`);

      setJobOffers(response.data.data || []);
      setPagination(response.data.pagination || { current: 1, pages: 1, total: 0 });

      // Calculate stats
      setStats({
        total: response.data.pagination?.total || 0,
        featured: (response.data.data || []).filter(offer => offer.isFeatured).length,
        totalViews: (response.data.data || []).reduce((sum, offer) => sum + (offer.viewsCount || 0), 0)
      });

      if (!selectedOffer && response.data.data && response.data.data.length > 0) {
        setSelectedOffer(response.data.data[0]);
      }

    } catch (err) {
      setError('Erreur lors du chargement');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const clearFilters = () => {
    setFilters({
      contractType: '',
      experienceLevel: '',
      isFeatured: ''
    });
    setSearchTerm('');
  };

  const handleArchiveOffer = async (offerId) => {
    if (window.confirm('Êtes-vous sûr de vouloir archiver cette offre ?')) {
      try {
        await api.put(`/jobOffer/${offerId}/archive`);
        await fetchJobOffers(pagination.current);
        if (selectedOffer?._id === offerId) {
          setSelectedOffer(null);
        }
      } catch (err) {
        console.error('Error:', err);
        alert("Erreur lors de l'archivage");
      }
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer définitivement cette offre ?')) {
      try {
        await api.delete(`/jobOffer/${offerId}`);
        await fetchJobOffers(pagination.current);
        if (selectedOffer?._id === offerId) {
          setSelectedOffer(null);
        }
      } catch (err) {
        console.error('Error:', err);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getExperienceLabel = (level) => {
    const labels = {
      1: 'Junior (0-2 ans)',
      2: 'Confirmé (2-5 ans)',
      3: 'Senior (5+ ans)',
      4: 'Expert (10+ ans)'
    };
    return labels[level] || 'Non spécifié';
  };

  const getContractTypeLabel = (type) => {
    const labels = {
      cdi: 'CDI',
      cdd: 'CDD',
      freelance: 'Freelance',
      internship: 'Stage'
    };
    return labels[type] || type;
  };

  if (loading && jobOffers.length === 0) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-10 w-10 text-primary-600 dark:text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Title + Stats */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-950/30 rounded-lg flex items-center justify-center">
                  <Briefcase className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                </div>
                <div>
                  <h1 className="text-base font-bold text-gray-900 dark:text-white">Mes Offres</h1>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('active')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'active'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 function hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                  Actives
                </button>
                <button
                  onClick={() => setActiveTab('archived')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'archived'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                  Archivées
                </button>
              </div>

              {/* Stats inline */}
              <div className="hidden md:flex items-center gap-2 text-xs">
                <div className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <span className="text-gray-700 dark:text-gray-300">{stats.total} offres</span>
                </div>
                <div className="px-2 py-1 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg">
                  <span className="text-yellow-700 dark:text-yellow-400">{stats.featured} en avant</span>
                </div>
                <div className="px-2 py-1 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <span className="text-blue-700 dark:text-blue-400">{stats.totalViews} vues</span>
                </div>
              </div>
            </div>

            {/* Right: Search + Actions */}
            <div className="flex items-center gap-2">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une offre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${showFilters
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                <Filter className="h-4 w-4" />
              </button>

              <button
                onClick={() => navigate('/dashboard/new-offer')}
                disabled={!limits.canCreate} // Disable if limit reached
                title={!limits.canCreate ? limits.message : "Créer une nouvelle offre"}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${!limits.canCreate
                  ? 'bg-gray-400 cursor-not-allowed text-gray-100'
                  : 'bg-primary-600 hover:bg-primary-700 text-white'
                  }`}
              >
                <Plus className="h-4 w-4" />
                Nouvelle Offre
              </button>
            </div>
          </div>

          {/* Filters Row */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={filters.contractType}
                  onChange={(e) => handleFilterChange('contractType', e.target.value)}
                  className="w-40 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Type de contrat</option>
                  <option value="cdi">CDI</option>
                  <option value="cdd">CDD</option>
                  <option value="freelance">Freelance</option>
                  <option value="internship">Stage</option>
                </select>
                <select
                  value={filters.experienceLevel}
                  onChange={(e) => handleFilterChange('experienceLevel', e.target.value)}
                  className="w-40 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Expérience</option>
                  <option value="1">Junior</option>
                  <option value="2">Confirmé</option>
                  <option value="3">Senior</option>
                  <option value="4">Expert</option>
                </select>
                <select
                  value={filters.isFeatured}
                  onChange={(e) => handleFilterChange('isFeatured', e.target.value)}
                  className="w-40 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Toutes</option>
                  <option value="true">En avant</option>
                  <option value="false">Standard</option>
                </select>
                <button
                  onClick={clearFilters}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                >
                  <X className="h-3.5 w-3.5" />
                  Effacer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className={`flex overflow-hidden ${showFilters ? 'h-[calc(100vh-180px)]' : 'h-[calc(100vh-120px)]'}`}>
        {/* Left - List */}
        <div className="w-80 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {jobOffers.length} offres
            </span>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {jobOffers.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="h-8 w-8 text-gray-300 dark:text-gray-700 mx-auto mb-2" />
                <p className="text-xs text-gray-500">Aucune offre</p>
              </div>
            ) : (
              <div className="space-y-2">
                {jobOffers.map((offer) => (
                  <button
                    key={offer._id}
                    onClick={() => setSelectedOffer(offer)}
                    className={`w-full p-3 rounded-lg transition-all text-left ${selectedOffer?._id === offer._id
                      ? 'bg-primary-50 dark:bg-primary-950/30 border border-primary-200 dark:border-primary-800'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'
                      }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate flex-1">
                        {offer.title.title}
                      </h4>
                      {offer.isFeatured && (
                        <Star className="h-4 w-4 text-yellow-500 flex-shrink-0 ml-2" />
                      )}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate mb-2">
                      {offer.address.city} • {getContractTypeLabel(offer.contractType)}
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Publié: {formatDate(offer.createdAt)}</span>
                      <span className="text-orange-500">Expire: {offer.endAt ? formatDate(offer.endAt) : 'Non défini'}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {pagination.pages > 1 && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>Page {pagination.current}/{pagination.pages}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchJobOffers(pagination.current - 1)}
                  disabled={pagination.current === 1}
                  className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Préc.
                </button>
                <button
                  onClick={() => fetchJobOffers(pagination.current + 1)}
                  disabled={pagination.current === pagination.pages}
                  className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
                >
                  Suiv.
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Center - Details */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950">
          {selectedOffer ? (
            <div className="p-6 max-w-4xl mx-auto">
              {/* Header Card */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 mb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {selectedOffer.title.title}
                      </h2>
                      {selectedOffer.isFeatured && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400 text-xs font-medium rounded-lg border border-yellow-200 dark:border-yellow-800">
                          <Star className="h-3 w-3" />
                          Mise en avant
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {selectedOffer.address.city}, {selectedOffer.address.wilaya}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {getContractTypeLabel(selectedOffer.contractType)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {getExperienceLabel(selectedOffer.experienceLevel)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {selectedOffer.viewsCount || 0} vues
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="prose dark:prose-invert max-w-none">
                  <p className="text-gray-700 dark:text-gray-300">
                    {selectedOffer.description}
                  </p>
                </div>
              </div>

              {/* Skills */}
              {selectedOffer.required_skills && selectedOffer.required_skills.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Compétences requises
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedOffer.required_skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary-50 dark:bg-primary-950/30 text-primary-700 dark:text-primary-400 rounded-lg text-sm font-medium border border-primary-200 dark:border-primary-800"
                      >
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              {selectedOffer.tags && selectedOffer.tags.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedOffer.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Info */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  Informations
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Date de publication</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatDate(selectedOffer.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Dernière mise à jour</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatDate(selectedOffer.updatedAt)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Date d'expiration</span>
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      {selectedOffer.endAt ? formatDate(selectedOffer.endAt) : 'Non défini'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Briefcase className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-500">Sélectionnez une offre</p>
              </div>
            </div>
          )}
        </div>

        {/* Right - Actions */}
        <div className="w-80 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
          {selectedOffer ? (
            <div className="p-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Actions rapides</h3>

              <div className="space-y-2">
                <button
                  onClick={() => navigate(`/dashboard/job-offers/${selectedOffer._id}`)}
                  className="w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Eye className="h-4 w-4" />
                  Voir les détails
                </button>
                <button
                  onClick={() => navigate(`/dashboard/job-offers/${selectedOffer._id}/edit`)}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Edit className="h-4 w-4" />
                  Modifier
                </button>

                {activeTab === 'active' ? (
                  <button
                    onClick={() => handleArchiveOffer(selectedOffer._id)}
                    className="w-full px-4 py-2.5 bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <Archive className="h-4 w-4" />
                    Archiver
                  </button>
                ) : (
                  <button
                    onClick={() => handleDeleteOffer(selectedOffer._id)}
                    className="w-full px-4 py-2.5 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <Trash2 className="h-4 w-4" />
                    Supprimer définitivement
                  </button>
                )}
              </div>

              {/* Stats */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3">Statistiques</h4>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Vues</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {selectedOffer.viewsCount || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Type</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {getContractTypeLabel(selectedOffer.contractType)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Expérience</span>
                    <span className="text-gray-900 dark:text-white font-medium text-right">
                      {getExperienceLabel(selectedOffer.experienceLevel)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-4">
              <p className="text-sm text-gray-500 text-center">Sélectionnez une offre</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyJobOffers;
